export const BOA_HORA_ROUTE = "/programa-saude-2/boa-hora";

export const BOA_HORA_LOCAL = "Boa Hora - Telemedicina";
export const BOA_HORA_SPECIALTY = "Enfermagem";
export const BOA_HORA_TIME_SLOTS = ["08:00", "09:00", "10:00", "11:00", "12:00", "13:00"];
export const BOA_HORA_MOCK_CHAMADO = "108389859";

export const BOA_HORA_CALENDAR_LOCALE = {
  id: "pt",
  firstDayOfWeek: 1,
  masks: {
    weekdays: "W",
    title: "MMMM YYYY"
  }
};

const PREGNANCY_DAYS = 280;
const MONTHS = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro"
];

export type BoaHoraAppointment = {
  isoDate: string;
  time: string;
  beneficiary: string;
  local: string;
  specialty: string;
};

export type BoaHoraRecord = {
  joined: boolean;
  dueDate: string | null;
  appointment: BoaHoraAppointment | null;
};

export type PregnancyImage = "trimestre-1" | "trimestre-2" | "trimestre-3" | "pos-parto";

export type PregnancySnapshot = {
  phase: "gestante" | "puerpera";
  image: PregnancyImage;
  headline: string;
  badge: string;
  message: string;
  messageCta: string | null;
  footerLabel: string;
  footerTone: "countdown" | "highlight" | "postpartum";
};

const emptyRecord = (): BoaHoraRecord => ({
  joined: false,
  dueDate: null,
  appointment: null
});

function storageKey(vinculo?: string | null) {
  const id = String(vinculo ?? "").replace(/\D/g, "") || "local";
  return `programaBoaHora.${id}`;
}

function startOfDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function diffDays(from: Date, to: Date) {
  const ms = startOfDay(to).getTime() - startOfDay(from).getTime();
  return Math.round(ms / 86400000);
}

export function formatPersonName(name?: string | null) {
  if (!name) return "";
  return String(name)
    .toLowerCase()
    .replace(/\b\w/g, match => match.toUpperCase());
}

export function formatIsoDate(isoDate?: string | null) {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  if (!year || !month || !day) return "";
  return `${day}/${month}/${year}`;
}

export function parseIsoDate(isoDate: string) {
  const [year, month, day] = isoDate.split("-").map(Number);
  return new Date(year, month - 1, day);
}

export function toIsoDate(date: Date) {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

export function readBoaHora(vinculo?: string | null): BoaHoraRecord {
  try {
    const raw = localStorage.getItem(storageKey(vinculo));
    if (!raw) return emptyRecord();
    const parsed = JSON.parse(raw);
    return {
      joined: !!parsed.joined,
      dueDate: parsed.dueDate || null,
      appointment: parsed.appointment || null
    };
  } catch {
    return emptyRecord();
  }
}

function writeBoaHora(vinculo: string | null | undefined, record: BoaHoraRecord) {
  localStorage.setItem(storageKey(vinculo), JSON.stringify(record));
}

export function saveBoaHora(vinculo: string | null | undefined, patch: Partial<BoaHoraRecord>) {
  const current = readBoaHora(vinculo);
  writeBoaHora(vinculo, { ...current, ...patch });
}

function plural(count: number, singular: string, pluralLabel: string) {
  return `${count} ${count === 1 ? singular : pluralLabel}`;
}

export function describePregnancy(dueDateIso: string, today = new Date()): PregnancySnapshot | null {
  if (!dueDateIso) return null;
  const dueDate = parseIsoDate(dueDateIso);
  if (Number.isNaN(dueDate.getTime())) return null;

  const daysUntilDue = diffDays(today, dueDate);
  if (daysUntilDue < 0) {
    const daysSinceBirth = Math.abs(daysUntilDue);
    const weeks = Math.max(1, Math.floor(daysSinceBirth / 7));
    return {
      phase: "puerpera",
      image: "pos-parto",
      headline: plural(daysSinceBirth, "dia", "dias"),
      badge: "Puérpera • Seu bebê já chegou",
      message: "Seu bebê já chegou e agora começa uma nova fase cheia de descobertas, cuidados e momentos especiais.",
      messageCta: null,
      footerLabel: `${plural(weeks, "semana", "semanas")} pós parto`,
      footerTone: "postpartum"
    };
  }

  const gestationalDays = Math.max(0, PREGNANCY_DAYS - daysUntilDue);
  const weeks = Math.max(1, Math.floor(gestationalDays / 7));
  const trimester = trimesterFromWeeks(weeks);
  let message =
    "Seu bebê está começando uma linda jornada! Sua gestação está avançando e seguiremos ao seu lado para apoiar você e seu bebê durante essa fase especial.";

  if (trimester.image === "trimestre-3") {
    message =
      "Falta pouco para conhecer seu bebê! O grande dia está chegando. Estamos preparados para acompanhar você neste momento tão importante.";
  } else if (trimester.image === "trimestre-2") {
    message =
      "Sua gestação está avançando e seguiremos ao seu lado para apoiar você e seu bebê durante essa fase especial.";
  }

  const lastWeek = daysUntilDue <= 7;
  return {
    phase: "gestante",
    image: trimester.image,
    headline: plural(weeks, "semana", "semanas"),
    badge: `Gestante • ${trimester.label}`,
    message,
    messageCta: "Agende sua primeira consulta para iniciar o acompanhamento.",
    footerLabel: lastWeek ? remainingSoonLabel(daysUntilDue) : plural(daysUntilDue, "dia", "dias"),
    footerTone: lastWeek ? "highlight" : "countdown"
  };
}

function trimesterFromWeeks(weeks: number) {
  if (weeks >= 28) return { label: "3º Trimestre", image: "trimestre-3" as const };
  if (weeks >= 14) return { label: "2º Trimestre", image: "trimestre-2" as const };
  return { label: "1º Trimestre", image: "trimestre-1" as const };
}

function remainingSoonLabel(days: number) {
  if (days <= 0) return "Hoje";
  if (days === 7) return "1 semana";
  return plural(days, "dia", "dias");
}

export function upcomingWeekdays(total = 24, today = new Date()) {
  const dates: Date[] = [];
  const cursor = startOfDay(today);
  cursor.setDate(cursor.getDate() + 1);
  while (dates.length < total) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) dates.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

export function getBoaHoraHomeTask(vinculo?: string | null) {
  const appointment = readBoaHora(vinculo).appointment;
  if (!appointment?.isoDate || !appointment.time) return null;
  const date = parseIsoDate(appointment.isoDate);
  return {
    isBoaHora: true,
    Chamado: BOA_HORA_MOCK_CHAMADO,
    DiaMesAgendamento: String(date.getDate()),
    MesAgendamentoExtenso: MONTHS[date.getMonth()],
    Hora: appointment.time,
    Assunto: appointment.local || BOA_HORA_LOCAL,
    TempoRestante: "Confira as orientações para a consulta",
    Situacao: 1
  };
}

export function formatLongDate(isoDate?: string | null) {
  if (!isoDate) return "";
  const date = parseIsoDate(isoDate);
  return `${date.getDate()} de ${MONTHS[date.getMonth()].toLowerCase()} de ${date.getFullYear()}`;
}
